// Port of src/app/app-routing.module.ts (react-router v7, library mode).
//
// Route table mirrors the Angular Routes array:
// - '', 'similar', 'artist'        → MainContentComponent → <MainContent/>
// - 'playlist/:artist'             → TopPlaylistsComponent (route param passed
//                                    as artistFromRoute)
// - 'top-playlists'                → TopPlaylistsComponent
// - 'signup', 'explorer-signup'    → LoginRegisterComponent. Angular rendered
//   the modal component directly at the router outlet with its default @Input
//   values (no MainContent behind it) — replicated by a tiny route component
//   that opens the LOGIN_REGISTER modal (default inputs) and renders nothing.
// - 'affiliate-program'            → AffiliationLinkComponent — same approach
//   with the AFFILIATION_LINK modal.
// - ':path/' → '/:path' trailing-slash redirect: react-router matches paths
//   with or without a trailing slash, so the Angular redirect rule is
//   inherently satisfied and needs no explicit route.
// - '**': Angular had no wildcard route (commented out) — an unmatched URL
//   left the outlet empty while the app shell (nav bar etc.) still rendered.
//   Mirrored with a catch-all route whose element is null.
//
// NOTE(port): the Angular config also lazy-loaded NavBarModule / PipeModule /
// DirectiveModule / SharedModule under '/navbar', '/pipes', '/directives',
// '/shared'. Those modules declared no child routes (navigating there could
// never render anything) and were all eagerly imported by AppModule anyway —
// vestigial registrations, not ported. ProfileModule was likewise eagerly
// imported in AppModule (Profile is an app-shell overlay, not a route), so no
// React.lazy is used anywhere — nothing was effectively lazy in Angular.
//
// basename: import.meta.env.BASE_URL → '/' in dev, '/songhunt/' in the prod
// build (vite.config.ts `base`), matching Angular's --base-href=/songhunt/.

import { useEffect } from 'react';
import { createBrowserRouter, useLocation, useParams } from 'react-router';
import App from '../App';
import MainContent from '../features/main-content/MainContent';
import TopPlaylists from '../features/top-playlists/TopPlaylists';
import { useModalStore } from '../stores/modal-store';
import { ModalType } from '../interfaces/modal-types.enum';

/**
 * Angular's ActivatedRoute.queryParams subscription (inside MainContent)
 * re-emitted when the query string changed; MainContent re-runs
 * handleQueryParams when its routeKey prop changes — keyed on location.search.
 */
function MainContentRoute() {
  const location = useLocation();
  return <MainContent routeKey={location.search} />;
}

function TopPlaylistsRoute() {
  const { artist } = useParams();
  return <TopPlaylists artistFromRoute={artist} />;
}

/**
 * Angular routed LoginRegisterComponent directly at the outlet — the modal
 * markup rendered with its default @Input values and nothing else in the
 * outlet. (MainContent — and with it the /signup Overwolf-input handling in
 * its ngOnInit — was NOT mounted on these routes in Angular.)
 */
function LoginRegisterRoute() {
  useEffect(() => {
    useModalStore.getState().open(ModalType.LOGIN_REGISTER);
  }, []);
  return null;
}

/** Angular routed AffiliationLinkComponent directly at the outlet. */
function AffiliationLinkRoute() {
  useEffect(() => {
    useModalStore.getState().open(ModalType.AFFILIATION_LINK);
  }, []);
  return null;
}

export const router = createBrowserRouter(
  [
    {
      // App is the shell (port of app.component.html): NavBar / Playlist /
      // TopPlaylists overlay / AdminPanel / Profile / ModalHost / Toasts live
      // outside route switching; the routed content renders in its <Outlet/>.
      path: '/',
      element: <App />,
      children: [
        { index: true, element: <MainContentRoute /> },
        { path: 'similar', element: <MainContentRoute /> },
        { path: 'artist', element: <MainContentRoute /> },

        // Dynamic playlists route
        { path: 'playlist/:artist', element: <TopPlaylistsRoute /> },
        { path: 'top-playlists', element: <TopPlaylistsRoute /> },
        { path: 'signup', element: <LoginRegisterRoute /> },
        { path: 'explorer-signup', element: <LoginRegisterRoute /> },
        { path: 'affiliate-program', element: <AffiliationLinkRoute /> },

        // Unmatched URLs: empty outlet, shell still renders (see header note).
        { path: '*', element: null },
      ],
    },
  ],
  { basename: import.meta.env.BASE_URL }
);
